import { useEffect } from 'react';
import { useUndoStore } from '../lib/undoStack';

/**
 * Is the event aimed at a field that owns its own native undo? When the
 * user is typing in an input / textarea / contenteditable we must NOT
 * hijack Cmd+Z — the browser's per-field undo is what they expect there.
 * App-level undo only fires when focus is outside any such field.
 */
function isTextEditingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return el.isContentEditable;
}

/**
 * Global keyboard listener for app-level Undo / Redo.
 *
 *   - Cmd+Z (mac) / Ctrl+Z (win/linux)            → undo
 *   - Cmd+Shift+Z / Ctrl+Shift+Z (or Ctrl+Y)      → redo
 *
 * Mount once high in the tree (see {@link ProjectLayout}). No-op while a
 * text field is focused so native undo wins.
 */
export function useUndoHotkey(): void {
  const undo = useUndoStore((s) => s.undo);
  const redo = useUndoStore((s) => s.redo);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      const key = e.key.toLowerCase();
      const isUndoKey = key === 'z';
      const isRedoKey = key === 'y'; // Windows-style redo
      if (!isUndoKey && !isRedoKey) return;

      // Defer to native field-level undo while editing text.
      if (
        isTextEditingTarget(e.target) ||
        isTextEditingTarget(document.activeElement)
      ) {
        return;
      }

      e.preventDefault();
      if (isRedoKey || (isUndoKey && e.shiftKey)) {
        void redo();
      } else {
        void undo();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [undo, redo]);
}
